import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { useAuth } from '../context/AuthContext';
import * as activityApi from '../services/activityApi';
import { formatDateTime } from '../utils/dateFormat';
import DateInput from '../components/DateInput';

const FIELD = {
  padding: '7px 10px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
};
const TH = { textAlign: 'left', verticalAlign: 'bottom', padding: '8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'middle' };
const STATUS = {
  online: { label: 'Online', color: 'var(--color-success)' },
  logged_out: { label: 'Logged out', color: 'var(--color-text-muted)' },
  ended: { label: 'Ended (no logout)', color: 'var(--color-warning)' },
};

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return ymd(d); };

export function fmtDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${pad(m)}m` : `${m}m`;
}

function fmtDateTime(iso) {
  return formatDateTime(iso);
}

export function deviceOf(ua) {
  const s = String(ua || '');
  if (!s) return '—';
  let browser = 'Browser';
  if (/Edg\//.test(s)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(s)) browser = 'Opera';
  else if (/Firefox\//.test(s)) browser = 'Firefox';
  else if (/Chrome\//.test(s)) browser = 'Chrome';
  else if (/Safari\//.test(s)) browser = 'Safari';
  let os = '';
  if (/Windows/.test(s)) os = 'Windows';
  else if (/Android/.test(s)) os = 'Android';
  else if (/iPhone|iPad|iOS/.test(s)) os = 'iOS';
  else if (/Mac OS X|Macintosh/.test(s)) os = 'Mac';
  else if (/Linux/.test(s)) os = 'Linux';
  return os ? `${browser} · ${os}` : browser;
}

function Badge({ status }) {
  const st = STATUS[status] || STATUS.ended;
  return <span style={{ color: st.color, fontWeight: 600, whiteSpace: 'nowrap' }}>{`● ${st.label}`}</span>;
}

function Tile({ label, value }) {
  return (
    <div style={{ flex: '1 1 150px', padding: '10px 14px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-surface)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{label}</div>
      <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

export default function LoginActivityPage() {
  const { user } = useAuth();
  const isAdmin = Boolean(user && user.role && (user.role.code === 'admin' || user.role.code === 'super_admin'));
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(ymd(new Date()));
  const [userId, setUserId] = useState('');
  const [summary, setSummary] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!isAdmin) return Promise.resolve();
    const base = {};
    if (from) base.from = new Date(`${from}T00:00:00`).toISOString();
    if (to) {
      const end = new Date(`${to}T00:00:00`);
      end.setDate(end.getDate() + 1);
      base.to = end.toISOString();
    }
    const withUser = userId ? { ...base, userId } : base;
    return Promise.all([activityApi.getSummary(base), activityApi.getSessions(withUser)])
      .then(([sum, ses]) => {
        setSummary(sum.items);
        setSessions(ses.items);
        setTruncated(ses.items.length >= ses.limit);
        setError(null);
      })
      .catch((err) => setError(err.message || 'Could not load login activity.'))
      .finally(() => setLoading(false));
  }, [isAdmin, from, to, userId]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  const totals = useMemo(() => ({
    sessions: summary.reduce((a, r) => a + r.sessions, 0),
    users: summary.length,
    active: summary.reduce((a, r) => a + r.activeSeconds, 0),
    online: summary.filter((r) => r.online).length,
  }), [summary]);

  const preset = (days) => { setFrom(daysAgo(days)); setTo(ymd(new Date())); };

  if (!isAdmin) {
    return (
      <div>
        <h1>Login Activity</h1>
        <Card><p>Aa page fakt admin mate chhe.</p></Card>
      </div>
    );
  }

  return (
    <div>
      <h1>Login Activity</h1>
      <p style={{ color: 'var(--color-text-secondary)' }}>
        Darek user nu login / logout ane system ma kitlo time kam karyu te. "Active time" ma fakt te time ganay chhe jyare user sachu kam kari rahyo hoy (tab khulo ane vaparto hoy).
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '12px 0' }}>
        <Tile label="Sessions" value={totals.sessions} />
        <Tile label="Users" value={totals.users} />
        <Tile label="Total active time" value={fmtDuration(totals.active)} />
        <Tile label="Online now" value={totals.online} />
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <Button variant="secondary" onClick={() => preset(0)}>Today</Button>
          <Button variant="secondary" onClick={() => preset(6)}>7 days</Button>
          <Button variant="secondary" onClick={() => preset(29)}>30 days</Button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            From
            <DateInput aria-label="From date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} style={FIELD} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            To
            <DateInput aria-label="To date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} style={FIELD} />
          </label>
          <select aria-label="User filter" value={userId} onChange={(e) => setUserId(e.target.value)} style={FIELD}>
            <option value="">All users</option>
            {summary.map((r) => <option key={r.userId} value={String(r.userId)}>{r.fullName || r.email || `#${r.userId}`}</option>)}
          </select>
          <Button variant="secondary" onClick={() => { setLoading(true); load(); }}>Refresh</Button>
        </div>

        {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        ) : (
          <>
            <h3 style={{ margin: '4px 0 8px' }}>User-wise time</h3>
            <div className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH}>User</th>
                    <th style={TH}>Role</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Sessions</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Active time</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Session time</th>
                    <th style={TH}>Last login</th>
                    <th style={TH}>Now</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.length === 0 && (
                    <tr><td colSpan={7} style={{ ...TD, color: 'var(--color-text-muted)' }}>Aa period ma koi login nathi.</td></tr>
                  )}
                  {summary.map((r) => (
                    <tr key={r.userId}>
                      <td style={TD}>
                        <button
                          type="button"
                          onClick={() => setUserId(String(r.userId))}
                          title="Show only this user's sessions"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-accent)', textDecoration: 'underline', font: 'inherit', textAlign: 'left' }}
                        >
                          {r.fullName || r.email || `#${r.userId}`}
                        </button>
                        {r.email && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{r.email}</div>}
                      </td>
                      <td style={TD}>{r.roleName || r.roleCode || '—'}</td>
                      <td style={{ ...TD, textAlign: 'right' }} className="data-figure">{r.sessions}</td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }} className="data-figure">{fmtDuration(r.activeSeconds)}</td>
                      <td style={{ ...TD, textAlign: 'right' }} className="data-figure">{fmtDuration(r.sessionSeconds)}</td>
                      <td style={TD}>{fmtDateTime(r.lastLoginAt)}</td>
                      <td style={TD}>{r.online ? <Badge status="online" /> : <span style={{ color: 'var(--color-text-muted)' }}>Offline</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ margin: '18px 0 8px' }}>
              {userId ? 'Sessions (selected user)' : 'All sessions'}
              {userId && (
                <button
                  type="button"
                  onClick={() => setUserId('')}
                  style={{ marginLeft: 10, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', font: 'inherit', fontSize: 'var(--font-size-sm)' }}
                >
                  ✕ Show all users
                </button>
              )}
            </h3>
            <div className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH}>#</th>
                    <th style={TH}>User</th>
                    <th style={TH}>Login</th>
                    <th style={TH}>Logout</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Session time</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Active time</th>
                    <th style={TH}>Status</th>
                    <th style={TH}>IP</th>
                    <th style={TH}>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.length === 0 && (
                    <tr><td colSpan={9} style={{ ...TD, color: 'var(--color-text-muted)' }}>No sessions.</td></tr>
                  )}
                  {sessions.map((s, i) => (
                    <tr key={s.id}>
                      <td style={TD}>{i + 1}</td>
                      <td style={TD}>{s.fullName || s.email || `#${s.userId}`}</td>
                      <td style={TD}>{fmtDateTime(s.loginAt)}</td>
                      <td style={TD}>{s.logoutAt ? fmtDateTime(s.logoutAt) : '—'}</td>
                      <td style={{ ...TD, textAlign: 'right' }} className="data-figure">{fmtDuration(s.sessionSeconds)}</td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }} className="data-figure">{fmtDuration(s.activeSeconds)}</td>
                      <td style={TD}><Badge status={s.status} /></td>
                      <td style={TD}>{s.ip || '—'}</td>
                      <td style={TD}>{deviceOf(s.userAgent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {truncated && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Fakt latest 500 sessions batavi chhe — date range nani karo.</p>}
          </>
        )}
      </Card>
    </div>
  );
}
